/**
 * https://vuefire.vuejs.org/
 */
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import type { FirebaseApp } from "firebase/app";
import type { HttpsCallable } from "firebase/functions";
import { logger } from '@/utils/logger'

let functions: ReturnType<typeof getFunctions>;
let llmApiFunction: HttpsCallable<
  { messages: Array<{ role: string, content: string }>, metadata: { project_id: string } },
  StandardLLMResponse
>;
let revokeSessionsFunction: HttpsCallable<void, RevokeSessionsResponse>;

/** `revoke_sessions` (functions-auth, europe-west1): revokes the caller's refresh
 *  tokens server-side so sessions on other devices die at their next refresh
 *  (MYAC-1191 / CASA 2.2.1). `tokens_valid_after` is ms since the epoch. */
interface RevokeSessionsResponse {
  revoked: boolean;
  tokens_valid_after: number;
}

interface StandardLLMResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: {
        answer?: string;
        source?: string[] | string;
      };
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/** Wires the Cloud Functions client and the callables the app uses.
 *  Called from both init paths — the deployed one (`/__/firebase/init.json`) and
 *  the dev fallback — so a callable is reachable in every environment. Before
 *  MYAC-1191 only the dev fallback ran this, which left `getFirebaseFunctions()`
 *  undefined on every deployed build. */
function initFunctions(app: FirebaseApp): void {
  functions = getFunctions(app, 'europe-west1');
  llmApiFunction = httpsCallable<
    { messages: Array<{ role: string, content: string }>, metadata: { project_id: string } },
    StandardLLMResponse
  >(functions, 'standard_llm_api', { timeout: 9 * 60 * 1000 });
  // 3 s, not the SDK default: sign-out awaits this call, so the timeout is how
  // long a user waits for a logout when the function is down or slow. The call
  // normally returns in well under a second; past 3 s the store gives up, logs
  // and signs out locally anyway (MYAC-1191).
  revokeSessionsFunction = httpsCallable<void, RevokeSessionsResponse>(
    functions, 'revoke_sessions', { timeout: 3_000 },
  );
}

/** A registered App Check DEBUG token for a local dev server (`VITE_APPCHECK_DEBUG_TOKEN`
 *  in `.env.local`, registered in the Firebase console for the dev web app): the
 *  reCAPTCHA site key is bound to the deployed hosts, so on localhost every token
 *  fetch fails and each listener waits through the SDK's retries. Dev builds only. */
function installAppCheckDebugToken(): void {
  if (!import.meta.env.DEV) return
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined
  if (debugToken && debugToken.length > 0) {
    ;(self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken
  }
}
installAppCheckDebugToken()

export const getFirebaseApp = new Promise<FirebaseApp>(function (resolve, reject) {
  return fetch('/__/firebase/init.json')
    .then(async function (response) {
      const config = await response.json();
      const app = initializeApp(config);
      // Initialize App Check with reCAPTCHA v3 using site key from env
      try {
        const siteKey = import.meta.env.VITE_APPCHECK_RECAPTCHA_V3_SITE_KEY as string | undefined;
        if (siteKey && siteKey.length > 0) {
          initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(siteKey),
            isTokenAutoRefreshEnabled: true,
          });
        } else {
          logger.warn('App Check not initialized: missing VITE_APPCHECK_RECAPTCHA_V3_SITE_KEY');
        }
      } catch (err) {
        logger.warn('Failed to initialize App Check', err);
      }
      initFunctions(app);
      logger.debug(config);
      resolve(app);
    }).catch(async function () {
      if (import.meta.env.DEV) {
        const app = initializeApp(
          {
            "apiKey": "AIzaSyDiPCfu0qA6cOLi78niQCSHgO5RK-V6CuU",
            "appId": "1:903974413917:web:86f85e448067ed77544fb4",
            "authDomain": "myauditcorner-dev.firebaseapp.com",
            "databaseURL": "",
            "measurementId": "G-C8VXRYSRFK",
            "messagingSenderId": "903974413917",
            "projectId": "myauditcorner-dev",
            "storageBucket": "myauditcorner-dev.firebasestorage.app"
          });
        // Initialize App Check in dev as well (use real key; for debug provider, configure separately)
        try {
          const siteKey = import.meta.env.VITE_APPCHECK_RECAPTCHA_V3_SITE_KEY as string | undefined;
          // A page served from localhost (vite dev, a tunnel) is not a domain the
          // reCAPTCHA key knows: every token fetch fails and the console fills
          // with `appCheck/recaptcha-error` while nothing is enforced on dev.
          const localOrigin = ['localhost', '127.0.0.1'].includes(window.location.hostname);
          if (localOrigin) {
            logger.info('App Check skipped on a local origin');
          } else if (siteKey && siteKey.length > 0) {
            initializeAppCheck(app, {
              provider: new ReCaptchaV3Provider(siteKey),
              isTokenAutoRefreshEnabled: true,
            });
          } else {
            logger.warn('App Check not initialized (dev): missing VITE_APPCHECK_RECAPTCHA_V3_SITE_KEY');
          }
        } catch (err) {
          logger.warn('Failed to initialize App Check (dev)', err);
        }
        initFunctions(app);
        await fetch("http://localhost:4400", { signal: AbortSignal.timeout(250) }).then(() => {
          logger.info("Emulators running");
          const auth = getAuth(app);
          connectAuthEmulator(auth, "http://localhost:9099");

          // Firestore Emulator
          const firestore = getFirestore(app);
          connectFirestoreEmulator(firestore, "localhost", 8080);

          // Storage Emulator
          const storage = getStorage(app);
          connectStorageEmulator(storage, "localhost", 9199);

          // Functions Emulator
          connectFunctionsEmulator(functions, "localhost", 5001);
        }).catch(() => {
          logger.info("Emulators not running");
        })
        resolve(app);
      }
      reject("No Firebase Config Found");
    });
});

getFirebaseApp.then((app) => {
});

export const getFirebaseFunctions = () => functions;
export const getLLMApiFunction = () => llmApiFunction;
export const getRevokeSessionsFunction = () => revokeSessionsFunction;
